// src/rms/RmsApp.js
// ============================================================================
// RMS route subtree
// ============================================================================
// Mounted at /rms/* by the top-level App. Self-contained: it brings its own
// provider, shell and styles, so it can grow while the legacy marketplace code
// is removed around it.
// ============================================================================

import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { RmsProvider, useRms } from "./context/RmsContext";
import RmsLayout from "./components/RmsLayout";
import PinGate from "./components/PinGate";
import RequirePin from "./components/RequirePin";
import Dashboard from "./pages/Dashboard";
import VerticalPicker from "./pages/VerticalPicker";
import Inventory from "./pages/Inventory";
import Reservations from "./pages/Reservations";
import Customers from "./pages/Customers";
import Messages from "./pages/Messages";
import CheckoutFlow from "./pages/CheckoutFlow";
import CheckinFlow from "./pages/CheckinFlow";
import AgreementView from "./pages/AgreementView";
import Billing from "./pages/Billing";
import Reports from "./pages/Reports";
import Pricing from "./pages/Pricing";
import Availability from "./pages/Availability";
import Timeline from "./pages/Timeline";
import { ReportBug, RecommendFeature } from "./pages/Feedback";
import Settings from "./pages/Settings";
import Occupancy from "./pages/Occupancy";
import AccessLog from "./pages/AccessLog";
import Dispatch from "./pages/Dispatch";
import Certifications from "./pages/Certifications";
import "./styles/rms.css";

/**
 * Wraps a route in the operator shell once an operator record exists.
 *
 * The session check happens one level up in App.jsx (RequireRole); this only
 * handles "signed in, but hasn't chosen a vertical yet" by sending them to the
 * picker.
 */
function RequireOperator({ children }) {
  const { operator, loading } = useRms();
  const location = useLocation();

  if (loading) {
    return (
      <div className="rms-root" style={{ display: "grid", placeItems: "center", minHeight: "60vh" }}>
        <p style={{ color: "var(--rms-muted-strong)", fontSize: 14 }}>Loading your account…</p>
      </div>
    );
  }

  if (!operator) {
    return <Navigate to="/rms/start" replace state={{ from: location.pathname }} />;
  }

  // Inside the layout, so the shell is drawn behind the prompt and somebody can
  // see the business they are being asked to set a PIN for -- but over every
  // operator route, because there is no screen in here worth reaching without
  // one.
  return (
    <RmsLayout>
      <RequirePin>{children}</RequirePin>
    </RmsLayout>
  );
}

/*
 * RequireCapability lived here and is gone as of the shared-terminal design.
 *
 * It gated a route on the SESSION member's role, which is the wrong question
 * the moment one terminal is signed in all day for a whole shift: the owner
 * standing at a staff-level counter was turned away before anything asked for
 * their PIN. Every route it used to guard now opens on rms_elevate instead,
 * which resolves the PIN to a person and checks THAT person.
 *
 * Do not reintroduce it. A route that needs restricting needs a PIN gate or a
 * server-side refusal, not a check against whoever left the browser signed in.
 */

function RmsRoutes() {
  // The gate copy lives here rather than inside PinGate so the component stays
  // about behaviour and the wording stays with the route it belongs to.
  const { t } = useRms();

  return (
    <Routes>
      {/* Onboarding — deliberately outside RequireOperator: this is where an
          operator record gets created in the first place. */}
      <Route path="/start" element={<VerticalPicker />} />

      <Route path="/" element={<RequireOperator><Dashboard /></RequireOperator>} />
      <Route path="/inventory" element={<RequireOperator><Inventory /></RequireOperator>} />
      <Route path="/reservations" element={<RequireOperator><Reservations /></RequireOperator>} />
      <Route path="/customers" element={<RequireOperator><Customers /></RequireOperator>} />
      <Route path="/messages" element={<RequireOperator><Messages /></RequireOperator>} />
      <Route path="/checkout/:reservationId" element={<RequireOperator><CheckoutFlow /></RequireOperator>} />
      <Route path="/checkin/:agreementId" element={<RequireOperator><CheckinFlow /></RequireOperator>} />
      <Route path="/agreement/:documentId" element={<RequireOperator><AgreementView /></RequireOperator>} />
      {/* Pricing, Billing and Settings open on a PIN and NOT on the session's
          role. The counter terminal signs in once as a staff-level member and
          stays signed in, so asking the session whether it may open the rate
          card turns the owner away before anybody asks for their PIN.
          rms_elevate asks the right question, server-side; PinGate publishes
          the answer to the screen inside it. */}
      <Route path="/pricing" element={<RequireOperator><PinGate section="pricing" title={t("pinGate.pricingTitle", "Open the rate card")} explain={t("pinGate.pricingHelp", "Your PIN opens pricing and records that you changed it.")}><Pricing /></PinGate></RequireOperator>} />
      <Route path="/pricing/:tab" element={<RequireOperator><PinGate section="pricing" title={t("pinGate.pricingTitle", "Open the rate card")} explain={t("pinGate.pricingHelp", "Your PIN opens pricing and records that you changed it.")}><Pricing /></PinGate></RequireOperator>} />
      <Route path="/timeline" element={<RequireOperator><Timeline /></RequireOperator>} />
      <Route path="/availability" element={<RequireOperator><Availability /></RequireOperator>} />
      <Route path="/billing" element={<RequireOperator><PinGate section="billing" title={t("pinGate.billingTitle", "Open billing")} explain={t("pinGate.billingHelp", "Your PIN opens billing and records that you looked.")}><Billing /></PinGate></RequireOperator>} />
      {/* Reports needs no wrapper at all: rms_gl_entries has no SELECT grant, so
          the screen is empty until rms_ledger_entries accepts a PIN. The
          session-role guard here was the same mistake as the three above. */}
      <Route path="/reports" element={<RequireOperator><Reports /></RequireOperator>} />
      <Route path="/report-bug" element={<RequireOperator><ReportBug /></RequireOperator>} />
      <Route path="/recommend-feature" element={<RequireOperator><RecommendFeature /></RequireOperator>} />
      <Route path="/settings" element={<RequireOperator><PinGate section="settings" title={t("pinGate.settingsTitle", "Open settings")} explain={t("pinGate.settingsHelp", "Your PIN opens settings and records that you went in.")}><Settings /></PinGate></RequireOperator>} />
      <Route path="/settings/:tab" element={<RequireOperator><PinGate section="settings" title={t("pinGate.settingsTitle", "Open settings")} explain={t("pinGate.settingsHelp", "Your PIN opens settings and records that you went in.")}><Settings /></PinGate></RequireOperator>} />

      {/* Continuous-occupancy verticals */}
      <Route path="/occupancy" element={<RequireOperator><Occupancy /></RequireOperator>} />
      <Route path="/access" element={<RequireOperator><AccessLog /></RequireOperator>} />

      {/* Delivery + certification verticals */}
      <Route path="/dispatch" element={<RequireOperator><Dispatch /></RequireOperator>} />
      <Route path="/certifications" element={<RequireOperator><Certifications /></RequireOperator>} />

      <Route path="*" element={<Navigate to="/rms" replace />} />
    </Routes>
  );
}

function RmsApp() {
  return (
    <RmsProvider>
      <RmsRoutes />
    </RmsProvider>
  );
}

export default RmsApp;
export { RmsProvider };
